// /var/www/oszczedzam/client/src/components/calendar/TransactionDetails.js
import React, { useState } from 'react';
import { 
  Box, Typography, Button, Chip, Divider, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions,
  Paper, Grid 
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Event as EventIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  LocalOffer as TagIcon
} from '@mui/icons-material';
import TransactionForm from '../transactions/TransactionForm';

function TransactionDetails({ transaction, onEdit, onDelete, onClose }) {
  const [openEditForm, setOpenEditForm] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
  };
  
  // Formatuj datę
  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('pl-PL', options);
  };
  
  // Otwórz formularz edycji
  const handleEdit = () => {
    setOpenEditForm(true);
  };
  
  // Zamknij formularz edycji
  const handleCloseEditForm = () => {
    setOpenEditForm(false);
  };
  
  // Zatwierdź edycję
  const handleSubmitEdit = (updatedData) => {
    onEdit({ ...transaction, ...updatedData });
    setOpenEditForm(false);
  };
  
  // Otwórz dialog potwierdzenia usunięcia
  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };
  
  // Zatwierdź usunięcie
  const handleConfirmDelete = () => {
    onDelete(transaction.id);
    setDeleteDialogOpen(false);
  };

  return (
    <Paper elevation={0}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2">
            {transaction.title}
          </Typography>
          
          <Box>
            <Button 
              startIcon={<EditIcon />} 
              onClick={handleEdit}
              sx={{ mr: 1 }}
            >
              Edytuj
            </Button>
            <Button 
              startIcon={<DeleteIcon />} 
              color="error"
              onClick={handleOpenDeleteDialog}
            >
              Usuń
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={3}>
          {/* Kwota */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {transaction.amount >= 0 ? (
                <TrendingUpIcon color="success" sx={{ mr: 1 }} />
              ) : (
                <TrendingDownIcon color="error" sx={{ mr: 1 }} />
              )}
              <Typography variant="body1" sx={{ mr: 1 }}>Kwota:</Typography>
              <Typography 
                variant="h6" 
                component="span"
                color={transaction.amount >= 0 ? 'success.main' : 'error.main'}
                fontWeight="bold"
              >
                {formatAmount(transaction.amount)}
              </Typography>
            </Box>
          </Grid>
          
          {/* Data */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EventIcon sx={{ mr: 1 }} color="action" />
              <Typography variant="body1" sx={{ mr: 1 }}>Data:</Typography>
              <Typography variant="body1" component="span" fontWeight="medium">
                {formatDate(transaction.date)}
              </Typography>
            </Box>
          </Grid>
          
          {/* Kategoria */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CategoryIcon sx={{ mr: 1 }} color="action" />
              <Typography variant="body1" sx={{ mr: 1 }}>Kategoria:</Typography>
              <Typography variant="body1" component="span" fontWeight="medium">
                {transaction.category}
              </Typography>
            </Box>
          </Grid>
          
          {/* Transakcja cykliczna */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EventIcon sx={{ mr: 1 }} color="action" />
              <Typography variant="body1" sx={{ mr: 1 }}>Cykliczność:</Typography>
              <Typography variant="body1" component="span">
                {transaction.isRecurring ? 'Transakcja cykliczna' : 'Transakcja jednorazowa'}
              </Typography>
            </Box>
          </Grid>
          
          {/* Opis */}
          {transaction.description && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <DescriptionIcon sx={{ mr: 1, mt: 0.5 }} color="action" />
                <Box>
                  <Typography variant="body1" component="div" sx={{ mb: 0.5 }}>Opis:</Typography>
                  <Typography variant="body2" component="div" sx={{ pl: 1 }}>
                    {transaction.description}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          
          {/* Tagi */}
          {transaction.tags && transaction.tags.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <TagIcon sx={{ mr: 1, mt: 0.5 }} color="action" />
                <Box>
                  <Typography variant="body1" component="div" sx={{ mb: 0.5 }}>Tagi:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {transaction.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button onClick={onClose}>Zamknij</Button>
        </Box>
      </Box>
      
      {/* Formularz edycji */}
      <Dialog open={openEditForm} onClose={handleCloseEditForm} maxWidth="md" fullWidth>
        <DialogContent>
          <TransactionForm 
            onSubmit={handleSubmitEdit}
            initialData={transaction}
            isEditing={true}
          />
        </DialogContent>
      </Dialog>
      
      {/* Dialog potwierdzenia usunięcia */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Potwierdź usunięcie</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz usunąć transakcję "{transaction.title}"? Tej operacji nie można cofnąć.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Anuluj</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default TransactionDetails;